<script>
import { format, isToday } from 'date-fns';
import LucideIcon from './LucideIcon.vue';

export default {
  components: {
    LucideIcon,
  },
  props: {
    message: { type: String, default: '' },
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    type: { 
      type: String, 
      default: 'info',
      validator: (value) => ['info', 'alert', 'error', 'success'].includes(value)
    },
    action: {
      type: [Object, String, Boolean, null],
      default: null,
    },
    todayText: {
      type: String,
      default: 'Today',
    },
  },
  computed: {
    displayTitle() {
      return this.title || this.message;
    },
    iconName() {
      const iconMap = {
        success: 'circle-check-big',
        error: 'circle-x',
        alert: 'circle-alert',
        info: 'info',
      };
      return iconMap[this.type] || 'info';
    },
    iconColor() {
      const colorMap = {
        success: '#10b981', // green-500
        error: '#ef4444', // red-500
        alert: '#f59e0b', // amber-500
        info: '#3b82f6', // blue-500
      };
      return colorMap[this.type] || colorMap.info;
    },
    formattedTimestamp() {
      const now = new Date();
      if (isToday(now)) {
        return `${this.todayText} ${format(now, 'h:mma')}`;
      }
      return format(now, 'MMM d, h:mma');
    },
  },
  methods: {
    closeToast() {
      this.$emit('close');
    },
    handleAction() {
      if (this.action && this.action.onClick) {
        this.action.onClick();
      }
      this.$emit('action-click', this.action);
    },
  },
};
</script>

<template>
  <div>
    <div
      class="shadow-sm bg-[#1A2036] dark:bg-[#1A2036] rounded-lg justify-between gap-3 flex mb-2 max-w-[25rem] min-h-[3.5rem] min-w-[15rem] px-4 py-3 items-start"
    >
      <div class="flex-shrink-0 pt-0.5">
        <LucideIcon 
          :name="iconName" 
          :size="20" 
          :color="iconColor"
        />
      </div>

      <div class="flex-1 flex flex-col gap-1 min-w-0 items-start">
        <div class="text-sm font-medium text-white dark:text-white leading-tight">
          {{ displayTitle }}
        </div>
        <div 
          v-if="description" 
          class="text-xs text-white/80 dark:text-white/80 text-left"
        >
          {{ description }}
        </div>
        <div class="flex items-center gap-2 text-white/60 dark:text-white/60 text-xs w-full justify-between">
          <span>{{ formattedTimestamp }}</span>
          <router-link
            v-if="action && action.type === 'link'"
            :to="action.to"
            class="font-medium cursor-pointer select-none text-hichat-500 dark:text-hichat-500 hover:text-hichat-600 dark:hover:text-hichat-600 text-xs"
          >
            {{ action.message }}
          </router-link>
          <button
            v-else-if="action && action.type === 'button'"
            @click="handleAction"
            class="font-medium cursor-pointer select-none text-hichat-500 dark:text-hichat-500 hover:text-hichat-600 dark:hover:text-hichat-600 text-xs px-2 py-0.5 rounded hover:bg-white/10 transition-colors"
          >
            {{ action.message || action.label }}
          </button>
        </div>
      </div>

      <button
        @click="closeToast"
        class="flex-shrink-0 text-white/70 hover:text-white transition-colors cursor-pointer p-0.5 hover:bg-white/10 rounded-full"
        aria-label="Close"
      >
        <LucideIcon name="x" :size="16" />
      </button>

    </div>
  </div>
</template>